import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { confirm } from "../../../../components/modal/comfirm";
import { ONBOARDING_PATH, ROOT_PATH } from "../../../../routes/constants";
import { reset } from "../../../auth/redux/auth-slice";
import { registerDeviceForPushNotification } from "../../../../utils/notification-utils";
import { unregisterNotificationDevice } from "../../../notification/redux/notification-slice";
import { getDeviceAndBrowserType } from "../../../../utils/hooks/use-a2hs";
import { isStandalone } from "../../../../utils/standalone";
import { showIosPWAReminder, showDeniedReminder } from "./web-push-confirm.utils";
import { SESSION_STORAGE_KEY } from "../../../../utils/session-storage";
import { LOCAL_STORAGE_KEY } from "../../../../utils/local-storage";
import { isBrowserSupported } from "../../../../firebase-config";

interface HookProps {}
export const useSetting = () => {
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isPopupOpen, setPopupOpen] = useState(false);
  const [pushNotificationEnabled, setPushNotificationEnabled] = useState(false);
  const [toggling, setToggling] = useState(false);
  const pushToken = useSelector((state: any) => state.notification.pushToken);

  useEffect(() => {
    if (!isBrowserSupported()) {
      console.warn("Push notifications are not supported in this browser");
      return;
    }

    const pushNotificationEnabled = Notification.permission === "granted" && !!pushToken;
    setPushNotificationEnabled(pushNotificationEnabled);
  }, [pushToken]);

  const clickSetting = () => {};
  const clickLanguage = () => {
    setPopupOpen(!isPopupOpen);
  };
  const clickDisclaimer = () => {
    navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/protocol`, {
      state: { title: t("Common.disclaimer"), des: t("Settings.disclaimer_content") },
    });
  };
  const clickPrivacy = () => {
    navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/protocol`, {
      state: { title: t("Common.privacy_notice"), des: t("Settings.privacy_policy_content") },
    });
  };
  const clickClose = () => {
    navigate(-1);
  };
  const clickLogout = () => {
    confirm({
      title: t("Common.are_you_sure_you_want_to_log_out"),
      message: "",
      confirmButtonText: t("global.text.confirm"),
      cancelButtonText: t("Cancel"),
    }).then((result) => {
      if (result.ok) {
        dispatch<any>(unregisterNotificationDevice());
        // TODO: call api to logout
        sessionStorage.removeItem(SESSION_STORAGE_KEY.AUTH_TOKEN);
        dispatch(reset());
        navigate(`${ROOT_PATH}/login`);
      }
    });
  };
  const togglePushNotification = useCallback(async () => {
    setToggling(true);
    const { device } = getDeviceAndBrowserType();
    if (device === "ios") {
      const isInPWAMode = isStandalone();
      if (!isInPWAMode) {
        showIosPWAReminder(t);
        setToggling(false);
        return;
      }
    }
    const permission = isBrowserSupported() ? Notification.permission : undefined;
    if (permission === "denied") {
      showDeniedReminder(t);
    } else {
      if (pushToken) {
        await dispatch<any>(unregisterNotificationDevice());
        localStorage.setItem(LOCAL_STORAGE_KEY.user_toggle_web_push, "off");
      } else {
        await registerDeviceForPushNotification({
          dispatch,
          rejectCb: () => {
            showDeniedReminder(t);
          },
        });
        localStorage.setItem(LOCAL_STORAGE_KEY.user_toggle_web_push, "on");
      }
    }
    setToggling(false);
  }, [dispatch, pushToken, t]);

  return {
    isPopupOpen,
    clickSetting,
    clickLanguage,
    clickDisclaimer,
    clickPrivacy,
    clickLogout,
    clickClose,
    togglePushNotification,
    pushNotificationEnabled,
    toggling,
  };
};
